import { Delete, DragIndicator, ErrorOutline } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { BuilderFieldDef } from "../../types/formBuilder.types";
import { builderFieldTypeLabelKey, getFieldTypeDefinition } from "../shared/FieldTypeRegistry";

interface CanvasFieldProps {
    field: BuilderFieldDef;
    selected: boolean;
    index: number;
    total: number;
    dragging: boolean;
    onSelect: (name: string) => void;
    onRemove: (name: string) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
    onDragStart: (index: number) => void;
    onDragOver: (event: DragEvent<HTMLDivElement>) => void;
    onDrop: (event: DragEvent<HTMLDivElement>, index: number) => void;
    onDragEnd: () => void;
}

const CanvasField = ({ field, selected, index, total, dragging, onSelect, onRemove, onMove, onDragStart, onDragOver, onDrop, onDragEnd }: CanvasFieldProps) => {
    const { t } = useTranslation();
    const definition = getFieldTypeDefinition(field.type);

    return (
        <Box
            className={`service-builder__canvas-field${selected ? " service-builder__canvas-field--selected" : ""}${dragging ? " service-builder__canvas-field--dragging" : ""}`}
            onClick={(event) => { event.stopPropagation(); onSelect(field.name); }}
            role="button"
            tabIndex={0}
            draggable
            onDragStart={(event: DragEvent<HTMLDivElement>) => {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", field.name);
                onDragStart(index);
            }}
            onDragOver={onDragOver}
            onDrop={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                onDrop(event, index);
            }}
            onDragEnd={onDragEnd}
        >
            <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                <DragIndicator className="service-builder__drag-icon" fontSize="small" />
                <Box minWidth={0} flex={1} className="service-builder__field-copy">
                    <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                        <Typography className="service-builder__field-label" noWrap title={field.label ? String(t(field.label)) : field.name}>
                            {field.label ? t(field.label) : field.name}
                        </Typography>
                        {field.required && <Chip size="small" label={t("Required")} color="primary" variant="outlined" />}
                        {field.hasRuleWarnings && (
                            <Tooltip title={field.ruleWarnings.join("\n")}>
                                <ErrorOutline color="warning" fontSize="small" />
                            </Tooltip>
                        )}
                    </Stack>
                    <Typography className="service-builder__field-meta" noWrap>
                        {definition?.label ? t(builderFieldTypeLabelKey(field.type, definition.label), { defaultValue: definition.label }) : field.type}
                    </Typography>
                </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={t("Move up")}>
                    <span>
                        <IconButton size="small" aria-label={String(t("Move {{field}} up", { field: field.label ? t(field.label) : field.name }))} disabled={index === 0} onClick={(event) => { event.stopPropagation(); onMove(index, index - 1); }}>
                            ↑
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t("Move down")}>
                    <span>
                        <IconButton size="small" aria-label={String(t("Move {{field}} down", { field: field.label ? t(field.label) : field.name }))} disabled={index >= total - 1} onClick={(event) => { event.stopPropagation(); onMove(index, index + 1); }}>
                            ↓
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t("Remove field")}>
                    <IconButton size="small" color="error" onClick={(event) => { event.stopPropagation(); onRemove(field.name); }}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
};

export default CanvasField;
